import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "fitsme_cart_v1";
const SYNC_DEBOUNCE_MS = 1200;

export const CartProvider = ({ children }) => {
    const { user } = useAuth();
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const syncTimer = useRef(null);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    // Debounced backend snapshot for abandoned-cart recovery. Only runs when we
    // have an email (signed-in user) — guests stay client-side only. The backend
    // collapses snapshots by email so spamming this is safe.
    useEffect(() => {
        const email = user?.email;
        if (!email) return undefined;
        if (syncTimer.current) clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
            api.post("/cart/snapshot", { email, items }).catch(() => {
                // Best-effort — recovery is a nicety, never block UX on it.
            });
        }, SYNC_DEBOUNCE_MS);
        return () => {
            if (syncTimer.current) clearTimeout(syncTimer.current);
        };
    }, [items, user?.email]);

    const addItem = useCallback((product, size, quantity = 1) => {
        setItems((prev) => {
            const idx = prev.findIndex(
                (i) => i.product_id === product.id && i.size === size,
            );
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
                return copy;
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    size,
                    quantity,
                    price: product.price,
                    image_url: product.image_url,
                    store_name: product.store_name,
                    brand: product.brand,
                },
            ];
        });
        setDrawerOpen(true);
    }, []);

    const updateQty = useCallback((product_id, size, quantity) => {
        setItems((prev) =>
            prev
                .map((i) =>
                    i.product_id === product_id && i.size === size
                        ? { ...i, quantity: Math.max(0, quantity) }
                        : i,
                )
                .filter((i) => i.quantity > 0),
        );
    }, []);

    const removeItem = useCallback((product_id, size) => {
        setItems((prev) =>
            prev.filter((i) => !(i.product_id === product_id && i.size === size)),
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    /** Merge a set of items into the cart without duplicating (product_id+size collapses).
     * Used by the /cart/restore magic-link flow so users can rehydrate their
     * abandoned bag from an email on any device.
     */
    const mergeItems = useCallback((incoming) => {
        if (!Array.isArray(incoming) || incoming.length === 0) return;
        setItems((prev) => {
            const map = new Map();
            for (const it of prev) map.set(`${it.product_id}::${it.size || ""}`, { ...it });
            for (const raw of incoming) {
                if (!raw || !raw.product_id) continue;
                const key = `${raw.product_id}::${raw.size || ""}`;
                const merged = {
                    product_id: raw.product_id,
                    name: raw.name,
                    size: raw.size,
                    quantity: Math.max(1, Number(raw.quantity) || 1),
                    price: Number(raw.price) || 0,
                    image_url: raw.image_url,
                    store_name: raw.store_name,
                    brand: raw.brand,
                };
                if (map.has(key)) {
                    // Take the higher quantity — user might have edited in either place.
                    const existing = map.get(key);
                    map.set(key, { ...existing, quantity: Math.max(existing.quantity, merged.quantity) });
                } else {
                    map.set(key, merged);
                }
            }
            return Array.from(map.values());
        });
    }, []);

    const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);

    const value = useMemo(
        () => ({ items, addItem, updateQty, removeItem, clear, mergeItems, total, count, drawerOpen, setDrawerOpen }),
        [items, addItem, updateQty, removeItem, clear, mergeItems, total, count, drawerOpen],
    );

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
